import React, { useState, useEffect, useCallback } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BUTIKKER = [
  { value: "royken", label: "Sport 1 Røyken" },
  { value: "slemmestad", label: "Sport 1 Slemmestad" },
];

const TOM_VARE = { ean: "", varenummer: "", artikkelnummer: "", produkt: "", merke: "", kategori: "", lager: { royken: 0, slemmestad: 0 } };

export default function Lager() {
  const [butikk, setButikk] = useState("");
  const [importing, setImporting] = useState(false);
  const [importFeil, setImportFeil] = useState("");
  const [importResultat, setImportResultat] = useState(null);

  const [query, setQuery] = useState("");
  const [varer, setVarer] = useState([]);
  const [søker, setSøker] = useState(false);

  const [redigerer, setRedigerer] = useState(null); // vare-objekt under redigering, eller null
  const [visNyForm, setVisNyForm] = useState(false);
  const [nyVare, setNyVare] = useState(TOM_VARE);
  const [lagrer, setLagrer] = useState(false);
  const [feilmelding, setFeilmelding] = useState("");

  const hentVarer = useCallback(async (søketekst) => {
    setSøker(true);
    try {
      const url = new URL(`${API_BASE_URL}/lager`);
      if (søketekst && søketekst.trim()) url.searchParams.set("q", søketekst.trim());
      const res = await fetch(url);
      const data = await res.json();
      setVarer(Array.isArray(data) ? data : []);
    } catch {
      setVarer([]);
    } finally {
      setSøker(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      hentVarer(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, hentVarer]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!butikk) {
      setImportFeil("Velg butikk før du laster opp filen");
      e.target.value = "";
      return;
    }
    setImporting(true);
    setImportFeil("");
    setImportResultat(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("butikk", butikk);
      const res = await fetch(`${API_BASE_URL}/lager/import`, { method: "POST", body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke importere filen");
      setImportResultat(data);
      hentVarer(query);
    } catch (err) {
      setImportFeil(err.message);
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  }

  async function lagreRedigering() {
    setLagrer(true);
    setFeilmelding("");
    try {
      const res = await fetch(`${API_BASE_URL}/lager/${redigerer._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(redigerer),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke lagre endringer");
      setRedigerer(null);
      hentVarer(query);
    } catch (err) {
      setFeilmelding(err.message);
    } finally {
      setLagrer(false);
    }
  }

  async function slettVare(id) {
    if (!window.confirm("Slette denne varen fra varelageret?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/lager/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Kunne ikke slette varen");
      hentVarer(query);
    } catch (err) {
      alert(err.message);
    }
  }

  async function opprettVare() {
    setLagrer(true);
    setFeilmelding("");
    try {
      const res = await fetch(`${API_BASE_URL}/lager`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nyVare),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke opprette varen");
      setVisNyForm(false);
      setNyVare(TOM_VARE);
      hentVarer(query);
    } catch (err) {
      setFeilmelding(err.message);
    } finally {
      setLagrer(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Varelager</h1>
      <p className="text-sm text-gray-500 mb-6">
        Den samlede varedatabasen for begge butikker. Importer beholdning fra backoffice, og
        søk/vedlikehold varene her. Dette skal på sikt erstatte Vareliste.
      </p>

      <div className="border border-gray-200 rounded-xl p-4 mb-8">
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">1. Velg hvilket butikklager filen gjelder</p>
          <div className="flex gap-4">
            {BUTIKKER.map((b) => (
              <label key={b.value} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="butikk"
                  value={b.value}
                  checked={butikk === b.value}
                  onChange={(e) => setButikk(e.target.value)}
                  disabled={importing}
                />
                {b.label}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Lagerantallet fra filen skrives kun til denne butikkens eget lagerfelt — den andre
            butikkens antall røres ikke. EAN, varenummer, artikkelnummer, produkt, merke og
            kategori er felles for begge butikker.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <label
            className={`px-3 py-1.5 rounded-lg border text-sm font-semibold whitespace-nowrap ${
              butikk && !importing
                ? "border-gray-300 hover:bg-gray-50 text-gray-700 cursor-pointer"
                : "border-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            2. {importing ? "Importerer …" : "Last opp lagerfil"}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleUpload}
              disabled={importing || !butikk}
            />
          </label>
          <p className="text-sm text-gray-500">
            {importFeil ? (
              <span className="text-red-600">{importFeil}</span>
            ) : importResultat ? (
              <>
                {importResultat.totalRader} rader lest, {importResultat.opprettet} nye,{" "}
                {importResultat.oppdatert} oppdatert
                {importResultat.hoppetOverUtenEan ? `, ${importResultat.hoppetOverUtenEan} hoppet over (mangler EAN)` : ""}
                {importResultat.feil?.length ? `, ${importResultat.feil.length} feilet` : ""}.
              </>
            ) : (
              "Ingen fil importert ennå."
            )}
          </p>
        </div>

        <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
          <p>
            Excel-fil fra backoffice, én rad per EAN. EAN er påkrevd — rader uten EAN hoppes over.
            Kolonneoverskrifter kan hete f.eks. EAN/Strekkode, Varenummer, Artikkelnummer,
            Navn/Produkt, Merke, Kategori, Lager/Antall — store/små bokstaver spiller ikke rolle.
          </p>
        </div>

        {importResultat?.feil?.length > 0 && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <p className="font-semibold mb-1">{importResultat.feil.length} rad(er) feilet:</p>
            <ul className="list-disc list-inside">
              {importResultat.feil.slice(0, 20).map((f, idx) => (
                <li key={idx}>
                  Rad {f.rad} (EAN {f.ean}): {f.melding}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3 gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Søk i varelageret: EAN, varenummer, artikkelnummer, kategori, merke eller produkt …"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={() => { setNyVare(TOM_VARE); setVisNyForm(true); setFeilmelding(""); }}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          + Legg til vare
        </button>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-3 py-2">EAN</th>
              <th className="text-left px-3 py-2">Varenummer</th>
              <th className="text-left px-3 py-2">Artikkelnummer</th>
              <th className="text-left px-3 py-2">Produkt</th>
              <th className="text-left px-3 py-2">Merke</th>
              <th className="text-left px-3 py-2">Kategori</th>
              <th className="text-right px-3 py-2">Røyken</th>
              <th className="text-right px-3 py-2">Slemmestad</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {varer.map((v) => (
              <tr key={v._id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">{v.ean}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.varenummer}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.artikkelnummer}</td>
                <td className="px-3 py-2">{v.produkt}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.merke}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.kategori}</td>
                <td className="px-3 py-2 text-right">{v.lager?.royken ?? 0}</td>
                <td className="px-3 py-2 text-right">{v.lager?.slemmestad ?? 0}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <button onClick={() => { setRedigerer(v); setFeilmelding(""); }} className="text-xs text-blue-600 hover:underline mr-3">
                    Rediger
                  </button>
                  <button onClick={() => slettVare(v._id)} className="text-xs text-red-600 hover:underline">
                    Slett
                  </button>
                </td>
              </tr>
            ))}
            {!søker && varer.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-400 text-sm">
                  {query ? "Ingen treff." : "Ingen varer i varelageret ennå."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!query && varer.length === 50 && (
        <p className="text-xs text-gray-400 mt-2">Viser de første 50 varene. Søk for å finne en spesifikk vare.</p>
      )}

      {redigerer && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Rediger vare</h2>
            <div className="space-y-3">
              <Felt label="EAN">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-gray-50" value={redigerer.ean} disabled />
              </Felt>
              <Felt label="Varenummer">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.varenummer || ""} onChange={(e) => setRedigerer({ ...redigerer, varenummer: e.target.value })} />
              </Felt>
              <Felt label="Artikkelnummer">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.artikkelnummer || ""} onChange={(e) => setRedigerer({ ...redigerer, artikkelnummer: e.target.value })} />
              </Felt>
              <Felt label="Produkt">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.produkt || ""} onChange={(e) => setRedigerer({ ...redigerer, produkt: e.target.value })} />
              </Felt>
              <Felt label="Merke">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.merke || ""} onChange={(e) => setRedigerer({ ...redigerer, merke: e.target.value })} />
              </Felt>
              <Felt label="Kategori">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.kategori || ""} onChange={(e) => setRedigerer({ ...redigerer, kategori: e.target.value })} />
              </Felt>
              <div className="grid grid-cols-2 gap-3">
                <Felt label="Antall Røyken">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.lager?.royken ?? 0} onChange={(e) => setRedigerer({ ...redigerer, lager: { ...redigerer.lager, royken: e.target.value } })} />
                </Felt>
                <Felt label="Antall Slemmestad">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={redigerer.lager?.slemmestad ?? 0} onChange={(e) => setRedigerer({ ...redigerer, lager: { ...redigerer.lager, slemmestad: e.target.value } })} />
                </Felt>
              </div>
            </div>
            {feilmelding && <p className="text-sm text-red-600 mt-3">{feilmelding}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setRedigerer(null)} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Avbryt</button>
              <button onClick={lagreRedigering} disabled={lagrer} className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-50">
                {lagrer ? "Lagrer …" : "Lagre"}
              </button>
            </div>
          </div>
        </div>
      )}

      {visNyForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Legg til vare</h2>
            <div className="space-y-3">
              <Felt label="EAN (påkrevd)">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.ean} onChange={(e) => setNyVare({ ...nyVare, ean: e.target.value })} />
              </Felt>
              <Felt label="Varenummer">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.varenummer} onChange={(e) => setNyVare({ ...nyVare, varenummer: e.target.value })} />
              </Felt>
              <Felt label="Artikkelnummer">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.artikkelnummer} onChange={(e) => setNyVare({ ...nyVare, artikkelnummer: e.target.value })} />
              </Felt>
              <Felt label="Produkt">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.produkt} onChange={(e) => setNyVare({ ...nyVare, produkt: e.target.value })} />
              </Felt>
              <Felt label="Merke">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.merke} onChange={(e) => setNyVare({ ...nyVare, merke: e.target.value })} />
              </Felt>
              <Felt label="Kategori">
                <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.kategori} onChange={(e) => setNyVare({ ...nyVare, kategori: e.target.value })} />
              </Felt>
              <div className="grid grid-cols-2 gap-3">
                <Felt label="Antall Røyken">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.lager.royken} onChange={(e) => setNyVare({ ...nyVare, lager: { ...nyVare.lager, royken: e.target.value } })} />
                </Felt>
                <Felt label="Antall Slemmestad">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={nyVare.lager.slemmestad} onChange={(e) => setNyVare({ ...nyVare, lager: { ...nyVare.lager, slemmestad: e.target.value } })} />
                </Felt>
              </div>
            </div>
            {feilmelding && <p className="text-sm text-red-600 mt-3">{feilmelding}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setVisNyForm(false)} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Avbryt</button>
              <button onClick={opprettVare} disabled={lagrer || !nyVare.ean.trim()} className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-50">
                {lagrer ? "Lagrer …" : "Opprett"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Felt({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
